import React, { useState, useEffect } from 'react';
import { apiClient } from '../../utils/apiClient';
import useViewport from '../../hooks/useViewport';
import AuthorAvatar from '../../components/shared/AuthorAvatar';

interface InvitationPromptProps {
  dossierTitle: string;
  dossierId: string;
  onAccept: () => void;
  onReject: () => void;
  allowBypass?: boolean;
  onBypass?: () => void;
}

interface IDossierPreview {
  _id: string;
  title: string;
  description: string;
  content: Array<{
    type: string;
    title: string;
    sections: Array<{
      blocks: Array<{
        type: string;
        content?: any;
        imageId?: string;
      }>;
    }>;
  }>;
  author: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    profilePicture?: string;
  };
  createdAt: string;
  updatedAt: string;
}

const InvitationPrompt: React.FC<InvitationPromptProps> = ({
  dossierTitle,
  dossierId,
  onAccept,
  onReject,
  allowBypass = false,
  onBypass
}) => {
  const [dossierPreview, setDossierPreview] = useState<IDossierPreview | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showPages, setShowPages] = useState(false);
  const { isMobile } = useViewport();

  useEffect(() => {
    const fetchDossierPreview = async () => {
      try {
        const response = await apiClient(`/dossiers/${dossierId}/preview`);

        if (response.ok) {
          const data = await response.json();
          setDossierPreview(data);
        }
      } catch (error) {
        console.error('Erreur lors de la récupération de l\'aperçu:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (dossierId) {
      fetchDossierPreview();
    }
  }, [dossierId]);

  // Animation des pages après le chargement
  useEffect(() => {
    if (!isLoading && dossierPreview) {
      const timer = setTimeout(() => {
        setShowPages(true);
      }, 300); // Petit délai avant l'animation
      return () => clearTimeout(timer);
    }
  }, [isLoading, dossierPreview]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-mcdm-primary-100 dark:bg-mcdm-third-400 flex justify-center items-center">
        <div className="text-mcdm-third-500 dark:text-mcdm-primary-100">Chargement de l'aperçu...</div>
      </div>
    );
  }

  return (
    <div className="h-screen max-h-screen bg-mcdm-primary-100 dark:bg-mcdm-third-400 flex overflow-hidden">
      {/* Sidebar - masquée en mobile */}
      {!isMobile && (
        <div className="w-1/5 bg-mcdm-primary-200 dark:bg-mcdm-third-300 p-6 flex flex-col justify-between">
          <div>
            <h2 className="text-xl text-mcdm-third-500 dark:text-mcdm-primary-100 mb-4 font-platypi">
              Invitation à collaborer
            </h2>

            <div className="mb-6">
              <h3 className="font-semibold text-mcdm-third-500 dark:text-mcdm-primary-100 mb-2">
                Dossier :
              </h3>
              <p className="text-sm text-mcdm-third-400 dark:text-mcdm-primary-200 font-medium">
                {dossierTitle}
              </p>
            </div>

            {dossierPreview && (
              <div className="mb-6">
                <h3 className="font-semibold text-mcdm-third-500 dark:text-mcdm-primary-100 mb-2">
                  Auteur :
                </h3>
                <div className="flex items-center">
                  <AuthorAvatar
                    user={{
                      _id: dossierPreview.author._id,
                      firstName: dossierPreview.author.firstName,
                      lastName: dossierPreview.author.lastName,
                      email: dossierPreview.author.email,
                      profilePicture: dossierPreview.author.profilePicture || null
                    }}
                    size="md"
                    className="mr-2"
                    showTooltip={false}
                  />
                  <div>
                    <p className="text-sm text-mcdm-third-400 dark:text-mcdm-primary-200">
                      {dossierPreview.author.firstName} {dossierPreview.author.lastName}
                    </p>
                    <p className="text-xs text-mcdm-third-300 dark:text-mcdm-primary-300">
                      {dossierPreview.author.email}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {dossierPreview && (
              <div className="mb-6">
                <h3 className="font-semibold text-mcdm-third-500 dark:text-mcdm-primary-100 mb-2">
                  Description :
                </h3>
                <p className="text-xs text-mcdm-third-400 dark:text-mcdm-primary-200 leading-relaxed">
                  {dossierPreview.description || 'Aucune description disponible'}
                </p>
              </div>
            )}

            {dossierPreview && (
              <div className="mb-6">
                <h3 className="font-semibold text-mcdm-third-500 dark:text-mcdm-primary-100 mb-2">
                  Dernière mise à jour :
                </h3>
                <p className="text-xs text-mcdm-third-400 dark:text-mcdm-primary-200">
                  {formatDate(dossierPreview.updatedAt)}
                </p>
              </div>
            )}
          </div>

          {/* Boutons d'action */}
          <div className="space-y-4">
            <button
              onClick={onAccept}
              className="w-full bg-mcdm-green-300 border border-mcdm-green-500 inset-shadow-sm inset-shadow-mcdm-green-500 text-shadow-xs hover:bg-mcdm-green-400 text-white py-3 px-4 rounded-lg transition-colors"
            >
              Accepter l'invitation
            </button>
            <button
              onClick={onReject}
              className="w-full bg-mcdm-primary-100 hover:bg-mcdm-primary-200 text-mcdm-third-500 py-3 px-4 rounded-lg font-medium transition-colors"
            >
              Refuser l'invitation
            </button>
          </div>
        </div>
      )}

      {/* Aperçu du contenu */}
      <div className={`${isMobile ? 'w-full' : 'w-4/5'} flex flex-col overflow-hidden`}>
        {/* En-tête fixe - centré en mobile */}
        <div className={`p-6 ${isMobile ? 'text-center' : 'text-center'}`}>
          <h1 className="text-2xl font-platypi text-mcdm-third-500 dark:text-mcdm-primary-100 mb-2">
            {isMobile ? 'Invitation à collaborer' : 'Aperçu du dossier'}
          </h1>
          <p className="text-mcdm-third-400 dark:text-mcdm-primary-200 text-sm">
            {isMobile ? `Dossier : ${dossierTitle}` : 'Voici un aperçu des premiers chapitres de ce dossier'}
          </p>
          {isMobile && dossierPreview && (
            <div className="flex flex-col items-center mt-2">
              <div className="flex items-center">
                <AuthorAvatar
                  user={{
                    _id: dossierPreview.author._id,
                    firstName: dossierPreview.author.firstName,
                    lastName: dossierPreview.author.lastName,
                    email: dossierPreview.author.email,
                    profilePicture: dossierPreview.author.profilePicture || null
                  }}
                  size="sm"
                  className="mr-1.5"
                  showTooltip={false}
                />
                <p className="text-mcdm-third-400 dark:text-mcdm-primary-200 text-xs">
                  par {dossierPreview.author.firstName} {dossierPreview.author.lastName}
                </p>
              </div>
              <p className="text-xs text-mcdm-third-300 dark:text-mcdm-primary-300 mt-0.5">
                {dossierPreview.author.email}
              </p>
            </div>
          )}
        </div>

        {/* Zone des pages empilées - alignement conditionnel */}
        <div className={`flex-1 flex justify-center relative ${isMobile ? 'pb-0 pt-10' : ''}`}>
          {dossierPreview && dossierPreview.content.length > 0 ? (
            <div className="relative flex justify-center">
              {dossierPreview.content.slice(0, 2).map((chapter, chapterIndex) => {
                const zIndex = 2 - chapterIndex;
                const translateY = chapterIndex * 8;
                const translateX = chapterIndex * 4;
                const isFirstPage = chapterIndex === 0;

                return (
                  <div
                    key={chapterIndex}
                    className="absolute bg-white shadow-2xl rounded-lg overflow-hidden transition-all duration-500 ease-out"
                    style={{
                      width: isMobile ? '240px' : '280px',
                      height: isMobile ? '320px' : '400px',
                      zIndex: zIndex,
                      left: isMobile ? '50%' : 'auto',
                      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2), 0 6px 10px rgba(0, 0, 0, 0.15)',
                      opacity: isFirstPage ? 1 : (showPages ? 1 : 0),
                      transform: isFirstPage
                        ? (isMobile
                          ? `translate(calc(-50% + ${translateX}px), ${translateY}px)`
                          : `translate(${translateX}px, ${translateY}px)`)
                        : (isMobile
                          ? `translate(calc(-50% + ${translateX}px), ${showPages ? translateY : translateY - 10}px)`
                          : `translate(${translateX}px, ${showPages ? translateY : translateY - 10}px)`)
                    }}
                  >
                    {/* En-tête du chapitre */}
                    <div className="bg-gray-50 border-b border-gray-200 p-4">
                      <h2 className={`${isMobile ? 'text-base' : 'text-lg'} font-bold text-mcdm-third-500 mb-1 truncate`}>
                        Chapitre {chapterIndex + 1}
                      </h2>
                      <p className="text-sm text-mcdm-third-400 truncate">
                        {chapter.title}
                      </p>
                      <div className="text-xs text-gray-500 mt-1">
                        Page {chapterIndex + 1}
                      </div>
                    </div>

                    {/* Contenu du chapitre */}
                    <div className="p-4 h-full overflow-hidden">
                      <div className="space-y-3">
                        {(chapter.sections ?? []).map((section, sectionIndex) => {
                          // Filet défensif : sections sans `blocks` (rare, ancienne donnée)
                          // ou refs Block orphelines (Block supprimé sans cleanup
                          // des refs côté Dossier, cf. incident historique sur les
                          // 12 dossiers d'avant l'activation de DossierHistory).
                          // Sans ce filtre, Mongoose populate renvoie [null, null, …]
                          // et le composant crashe à la première lecture de `block.type`.
                          const safeBlocks = (section.blocks ?? []).filter(
                            (b): b is NonNullable<typeof b> => b != null
                          );
                          return (
                            <div key={sectionIndex}>
                              {safeBlocks.slice(0, 3).map((block, blockIndex) => (
                                <div key={blockIndex} className="text-xs">
                                  {block.type === 'title' && (
                                    <h3 className="font-semibold text-mcdm-third-500 mb-1 truncate">
                                      {block.content}
                                    </h3>
                                  )}
                                  {block.type === 'text' && (
                                    <p className="text-mcdm-third-400 mb-2 line-clamp-2">
                                      {typeof block.content === 'string' ? block.content.slice(0, 80) : ''}
                                      {typeof block.content === 'string' && block.content.length > 80 ? '...' : ''}
                                    </p>
                                  )}
                                  {block.type === 'compositeText' && block.content?.blocks && (
                                    <p className="text-mcdm-third-400 mb-2 line-clamp-2">
                                      {block.content.blocks[0]?.rawText?.slice(0, 80)}
                                      {block.content.blocks[0]?.rawText?.length > 80 ? '...' : ''}
                                    </p>
                                  )}
                                  {block.type === 'image' && (
                                    <div className="w-full h-8 bg-gray-200 rounded mb-2 flex items-center justify-center">
                                      <span className="text-xs text-gray-500">Image</span>
                                    </div>
                                  )}
                                </div>
                              ))}
                              {safeBlocks.length > 3 && (
                                <div className="text-xs text-gray-500 italic">
                                  ... {safeBlocks.length - 3} autres éléments
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Pied de page */}
                    <div className="absolute bottom-0 left-0 right-0 bg-gray-50 border-t border-gray-200 p-2">
                      <div className="flex justify-between items-center text-xs text-gray-500">
                        <span className="truncate flex-1">{dossierTitle}</span>
                        <span className="ml-2">{formatDate(dossierPreview.createdAt)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Indicateur du nombre total de chapitres */}
              {dossierPreview.content.length > 2 && (
                <div
                  className="absolute bg-mcdm-secondary-400 text-white rounded-full w-8 h-8 flex items-center justify-center text-xs font-bold transition-all duration-500 ease-out"
                  style={{
                    top: '-10px',
                    right: '-10px',
                    zIndex: 10,
                    opacity: showPages ? 1 : 0,
                    transform: showPages ? 'scale(1)' : 'scale(0.8)'
                  }}
                >
                  +{dossierPreview.content.length - 2}
                </div>
              )}
            </div>
          ) : (
            <div className="text-center text-mcdm-third-400 dark:text-mcdm-primary-200">
              <div className="bg-mcdm-primary-200 dark:bg-mcdm-third-300 rounded-lg p-8 max-w-md">
                <p className="text-lg font-medium mb-2">Dossier vide</p>
                <p className="text-sm">Ce dossier ne contient pas encore de contenu.</p>
              </div>
            </div>
          )}
        </div>

        {/* Boutons d'action en mobile */}
        {isMobile && (
          <div className="p-6 space-y-4">
            <button
              onClick={onAccept}
              className="w-full bg-mcdm-green-300 border border-mcdm-green-500 inset-shadow-sm inset-shadow-mcdm-green-500 text-shadow-xs hover:bg-mcdm-green-400 text-white py-3 px-4 rounded-lg transition-colors"
            >
              Accepter l'invitation
            </button>
            <button
              onClick={onReject}
              className="w-full bg-mcdm-primary-100 hover:bg-mcdm-primary-200 text-mcdm-third-500 py-3 px-4 rounded-lg font-medium transition-colors"
            >
              Refuser l'invitation
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default InvitationPrompt;